import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Alert,
  Chip,
  Autocomplete,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Stack,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import dayjs from "dayjs";
import Loader from "../../components/Utility/Loader";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RfidEndorsementService from "../../service/RfidEndorsementService";

/* ==================== BRAND COLORS ==================== */
const BRAND = {
  main: "#004AAD",
  dark: "#003580",
  light: "#1A5FC4",
  soft: "#E6EEF9",
  softer: "#F4F8FD",
  border: "#C5D8F0",
  success: "#2E7D32",
  warning: "#E65100",
  danger: "#C62828",
};

const LOCATION_CLOCK_MAP = {
  Hambanthota: { IN: "28", OUT: "29" },
  Trincomalee: { IN: "30", OUT: "31" },
  KRY: { IN: "32", OUT: "33" },
};

const LOCATIONS = [
  { value: "Hambanthota", label: "Hambanthota" },
  { value: "Trincomalee", label: "Trincomalee" },
  { value: "KRY", label: "KRY" },
];

/* ==================== STYLED SNACKBAR ==================== */
const TopAlert = styled(Alert, {
  shouldForwardProp: (prop) => prop !== "alertseverity",
})(({ theme, alertseverity }) => {
  const colorMap = {
    success: { bg: BRAND.success, border: "#1B5E20" },
    error: { bg: BRAND.danger, border: "#8E0000" },
    warning: { bg: BRAND.warning, border: "#BF360C" },
    info: { bg: BRAND.main, border: BRAND.dark },
  };
  const c = colorMap[alertseverity] || colorMap.info;

  return {
    width: "100%",
    borderRadius: "14px",
    fontWeight: 700,
    fontSize: "14px",
    padding: "12px 16px",
    color: "#ffffff",
    background: `linear-gradient(135deg, ${c.bg} 0%, ${c.border} 100%)`,
    boxShadow: `0 12px 32px ${c.bg}55, 0 4px 12px rgba(0,0,0,0.15)`,
    border: `1px solid ${c.border}`,
    alignItems: "center",
    "& .MuiAlert-icon": {
      fontSize: "24px",
      color: "#ffffff",
      opacity: 1,
    },
    "& .MuiAlert-message": {
      padding: "2px 0",
      fontWeight: 700,
      letterSpacing: "0.2px",
    },
    "& .MuiAlert-action": {
      color: "#ffffff",
      paddingTop: 0,
      "& .MuiIconButton-root": {
        color: "#ffffff",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.18)",
        },
      },
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      padding: "10px 14px",
    },
  };
});

/* ==================== STYLED COMPONENTS ==================== */
const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${BRAND.softer} 0%, #ffffff 50%, ${BRAND.soft} 100%)`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const TabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  borderRadius: "10px",
  padding: theme.spacing(1.5, 3.5),
  fontSize: "14px",
  fontWeight: 700,
  textTransform: "none",
  letterSpacing: "0.5px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: active ? "none" : `1.5px solid ${BRAND.border}`,
  backgroundColor: active ? BRAND.main : "#ffffff",
  color: active ? "#ffffff" : BRAND.main,
  boxShadow: active
    ? `0 8px 20px rgba(0, 74, 173, 0.35)`
    : "0 2px 6px rgba(0,0,0,0.04)",
  "&:hover": {
    backgroundColor: active ? BRAND.dark : BRAND.soft,
    boxShadow: active
      ? `0 10px 24px rgba(0, 74, 173, 0.45)`
      : `0 4px 12px rgba(0, 74, 173, 0.12)`,
    transform: "translateY(-1px)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "13px",
    padding: theme.spacing(1.25, 2),
  },
}));

const SearchButton = styled(Button)(({ theme }) => ({
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: "13.5px",
  padding: theme.spacing(1, 2.5),
  borderColor: BRAND.main,
  color: BRAND.main,
  borderWidth: "1.5px",
  transition: "all 0.25s ease",
  "&:hover": {
    borderColor: BRAND.main,
    backgroundColor: BRAND.soft,
    borderWidth: "1.5px",
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px rgba(0, 74, 173, 0.25)`,
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    padding: theme.spacing(0.75, 1.5),
  },
}));

const EndorseButton = styled(Button)(({ theme }) => ({
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: "13.5px",
  padding: theme.spacing(1, 2.5),
  background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.light} 100%)`,
  boxShadow: `0 4px 14px rgba(0, 74, 173, 0.35)`,
  transition: "all 0.25s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.main} 100%)`,
    boxShadow: `0 6px 20px rgba(0, 74, 173, 0.5)`,
    transform: "translateY(-1px)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    padding: theme.spacing(0.75, 1.5),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: "1100px",
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: `0 10px 40px rgba(0, 74, 173, 0.10)`,
  border: `1px solid ${BRAND.border}`,
  backgroundColor: "#ffffff",
  [theme.breakpoints.down("sm")]: { borderRadius: "14px" },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  "& .MuiTableCell-head": {
    backgroundColor: BRAND.main,
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "12.5px",
    textAlign: "center",
    padding: theme.spacing(1.5, 1),
    borderBottom: "none",
    whiteSpace: "nowrap",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    [theme.breakpoints.down("sm")]: {
      fontSize: "10px",
      padding: "8px 4px",
      letterSpacing: "0.3px",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.25, 1),
  fontSize: "13.5px",
  borderBottom: `1px solid ${BRAND.soft}`,
  [theme.breakpoints.down("sm")]: {
    fontSize: "11.5px",
    padding: "8px 4px",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "all 0.2s ease",
  "&:nth-of-type(even)": { backgroundColor: BRAND.softer },
  "&:hover": { backgroundColor: BRAND.soft },
}));

const SearchField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#ffffff",
    transition: "all 0.25s ease",
    "&:hover fieldset": { borderColor: BRAND.main },
    "&.Mui-focused fieldset": { borderColor: BRAND.main, borderWidth: "2px" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: BRAND.main },
}));

const TimeBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant" && prop !== "synced",
})(({ variant, synced, theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "3px",
  padding: "3px 7px",
  borderRadius: "7px",
  fontSize: "12px",
  fontWeight: 700,
  backgroundColor:
    synced === true
      ? "#E8F5E9"
      : synced === false
      ? "#FFF3E0"
      : variant === "in"
      ? "#E8F5E9"
      : variant === "out"
      ? "#FFF3E0"
      : BRAND.soft,
  color:
    synced === true
      ? BRAND.success
      : synced === false
      ? BRAND.warning
      : variant === "in"
      ? BRAND.success
      : variant === "out"
      ? BRAND.warning
      : "#616161",
  whiteSpace: "nowrap",
  [theme.breakpoints.down("sm")]: {
    fontSize: "10.5px",
    padding: "2px 5px",
    gap: "2px",
    borderRadius: "6px",
  },
}));

/* ==================== TOTAL COUNT BADGE ==================== */
const CountBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(1.25, 2),
  borderRadius: "12px",
  background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.light} 100%)`,
  color: "#ffffff",
  boxShadow: `0 4px 14px rgba(0, 74, 173, 0.30)`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1, 1.5),
  },
}));

/* ==================== MAIN COMPONENT ==================== */
const Rfid_Attendence = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeSection, setActiveSection] = useState("attendance");
  const [selectedTab, setSelectedTab] = useState("Tab1");
  const [endorsements, setEndorsements] = useState({ cdl: [], subcontract: [] });
  const [endorsementsLoading, setEndorsementsLoading] = useState(false);
  const [endorsementsError, setEndorsementsError] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [startDate, setStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [serviceNumber, setServiceNumber] = useState("");
  const [rfidData, setRfidData] = useState([]);
  const [subcontractData, setSubcontractData] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("Hambanthota");
  const [loading, setLoading] = useState(true);
  const [searchMode, setSearchMode] = useState(false);
  const [expandedSearch, setExpandedSearch] = useState(false);
  const [triggerSearch, setTriggerSearch] = useState(false);
  const [cdlEmployees, setCdlEmployees] = useState([]);
  const [subEmployees, setSubEmployees] = useState([]);

  const [endorseOpen, setEndorseOpen] = useState(false);
  const [endorseForm, setEndorseForm] = useState({
    p_reason: "",
    p_hstatus: "I",
    P_SNO: "",
    p_location: "Hambanthota",
    P_DATE_DATE: dayjs().format("YYYY-MM-DD"),
    P_DATE_TIME: dayjs().format("HH:mm"),
  });
  const [endorseSubmitting, setEndorseSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const fetchEndorsementEmployees = async () => {
      try {
        const [cdlResponse, subResponse] = await Promise.all([
          axios.get("Rfid/GetCdlEMP"),
          axios.get("Rfid/GetSUbEMP"),
        ]);

        setCdlEmployees(cdlResponse.data?.ResultSet || []);
        setSubEmployees(subResponse.data?.ResultSet || []);
      } catch (error) {
        console.error("Failed to load endorsement employees:", error);
        setCdlEmployees([]);
        setSubEmployees([]);
      }
    };

    fetchEndorsementEmployees();
  }, []);

  useEffect(() => {
    if (activeSection !== "endorsements") return undefined;

    let isCurrent = true;
    const fetchEndorsements = async () => {
      setEndorsementsLoading(true);
      setEndorsementsError("");

      try {
        const [cdlResponse, subResponse] = await Promise.all([
          RfidEndorsementService.getCdlEndorsements(),
          RfidEndorsementService.getSubEndorsements(),
        ]);

        if (isCurrent) {
          setEndorsements({
            cdl: cdlResponse.data?.ResultSet || [],
            subcontract: subResponse.data?.ResultSet || [],
          });
        }
      } catch (error) {
        if (isCurrent) {
          setEndorsements({ cdl: [], subcontract: [] });
          setEndorsementsError(
            error.response?.data?.message ||
              error.message ||
              "Could not load endorsements."
          );
        }
      } finally {
        if (isCurrent) setEndorsementsLoading(false);
      }
    };

    fetchEndorsements();
    return () => {
      isCurrent = false;
    };
  }, [activeSection]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setSearchMode(false);
    setExpandedSearch(false);
  };

  const handleDateChange = (e) => setSelectedDate(e.target.value);
  const handleStartDateChange = (e) => setStartDate(e.target.value);
  const handleEndDateChange = (e) => setEndDate(e.target.value);
  const handleServiceNumberChange = (e) => setServiceNumber(e.target.value);
  const handleLocationChange = (e) => setSelectedLocation(e.target.value);

  const toggleSearchMode = () => {
    setSearchMode(!searchMode);
    if (!searchMode) setExpandedSearch(true);
  };
  const toggleExpandSearch = () => setExpandedSearch(!expandedSearch);

  const showEndorsements = () => {
    setEndorseOpen(false);
    setActiveSection("endorsements");
  };

  const showAttendance = () => setActiveSection("attendance");

  const openEndorseDialog = () => {
    setEndorseForm({
      p_reason: "",
      p_hstatus: "I",
      P_SNO: "",
      p_location: selectedLocation,
      P_DATE_DATE: dayjs().format("YYYY-MM-DD"),
      P_DATE_TIME: dayjs().format("HH:mm"),
    });
    setEndorseOpen(true);
  };

  const closeEndorseDialog = () => {
    if (endorseSubmitting) return;
    setEndorseOpen(false);
  };

  const handleEndorseFieldChange = (field, value) => {
    setEndorseForm((prev) => ({ ...prev, [field]: value }));
  };

  const getDeviceLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this device."));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
          });
        },
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });

  const getClockNo = (location, hstatus) => {
    const locMap = LOCATION_CLOCK_MAP[location];
    if (!locMap) {
      console.warn("Unknown location:", location);
      return null;
    }
    const key = hstatus === "I" ? "IN" : "OUT";
    return locMap[key] || null;
  };

  const handleEndorseSubmit = async () => {
    if (!endorseForm.P_SNO.trim()) {
      setSnackbar({
        open: true,
        message: selectedTab === "Tab1" ? "Service Number is required." : "User ID is required.",
        severity: "warning",
      });
      return;
    }
    if (!endorseForm.p_reason.trim()) {
      setSnackbar({
        open: true,
        message: "Reason is required.",
        severity: "warning",
      });
      return;
    }
    if (!endorseForm.P_DATE_DATE || !endorseForm.P_DATE_TIME) {
      setSnackbar({
        open: true,
        message: "Date and Time are required.",
        severity: "warning",
      });
      return;
    }

    const isSubcontract = selectedTab === "Tab2";
    const autoClockNo = isSubcontract
      ? null
      : getClockNo(endorseForm.p_location, endorseForm.p_hstatus);
    if (!isSubcontract && !autoClockNo) {
      setSnackbar({
        open: true,
        message: "Unable to determine Clock No for the selected location/status.",
        severity: "error",
      });
      return;
    }

    setEndorseSubmitting(true);
    try {
      let coords;
      try {
        coords = await getDeviceLocation();
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Unable to get device location. Please allow location access.",
          severity: "error",
        });
        setEndorseSubmitting(false);
        return;
      }

      const combinedDateTime = dayjs(
        `${endorseForm.P_DATE_DATE} ${endorseForm.P_DATE_TIME}`
      ).format("YYYY-MM-DD HH:mm:ss");

      const payload = isSubcontract
        ? {
            p_longitude: coords.longitude,
            p_latitude: coords.latitude,
            p_uid: endorseForm.P_SNO,
            p_tid: endorseForm.p_hstatus === "I" ? 1 : 2,
            P_DATE: combinedDateTime,
            p_location: endorseForm.p_location,
            p_reason: endorseForm.p_reason,
          }
        : {
            p_reason: endorseForm.p_reason,
            p_longitude: coords.longitude,
            p_latitude: coords.latitude,
            p_hstatus: endorseForm.p_hstatus,
            P_SNO: endorseForm.P_SNO,
            P_DATE: combinedDateTime,
            p_location: endorseForm.p_location,
            p_clockno: autoClockNo,
          };

      await axios.post(
        isSubcontract ? "Rfid/PostSubEndorsment" : "Rfid/PostCDLEndorsment",
        payload
      );

      setSnackbar({
        open: true,
        message: "Endorsement submitted successfully.",
        severity: "success",
      });
      setEndorseOpen(false);
      setTriggerSearch((prev) => !prev);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to submit endorsement. Please try again.",
        severity: "error",
      });
    } finally {
      setEndorseSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const getClockNos = () => {
        switch (selectedLocation) {
          case "Hambanthota":
            return { P_IN: "28", P_OUT: "29" };
          case "Trincomalee":
            return { P_IN: "30", P_OUT: "31" };
          case "KRY":
            return { P_IN: "32", P_OUT: "33" };
          default:
            return { P_IN: "01", P_OUT: "02" };
        }
      };

      const fetchRfidDetails = async () => {
        if (selectedDate) {
          const { P_IN, P_OUT } = getClockNos();
          try {
            const res = await axios.get(
              `Rfid/GetRfidDetails?P_DATE=${selectedDate}&P_IN=${P_IN}&P_OUT=${P_OUT}`
            );
            setRfidData(res.data.ResultSet || []);
          } catch {
            setRfidData([]);
          }
        }
      };

      const fetchRfidDetailsById = async () => {
        if (serviceNumber && startDate && endDate) {
          const { P_IN, P_OUT } = getClockNos();
          try {
            const res = await axios.get(
              `Rfid/GetRfidDetailsbyid?P_SDATE=${startDate}&P_EDATE=${endDate}&P_SNO=${serviceNumber}&P_IN=${P_IN}&P_OUT=${P_OUT}`
            );
            setRfidData(res.data.ResultSet || []);
          } catch {
            setRfidData([]);
          }
        }
      };

      const fetchSubcontractDetails = async () => {
        if (selectedDate) {
          try {
            const res = await axios.get(`Rfid/GetSubDetails?P_DATE=${selectedDate}`);
            setSubcontractData(res.data.ResultSet || []);
          } catch {
            setSubcontractData([]);
          }
        }
      };

      const fetchSubcontractDetailsById = async () => {
        if (serviceNumber && startDate && endDate) {
          try {
            const res = await axios.get(
              `Rfid/GetSubDetailsbyId?P_SDATE=${startDate}&P_EDATE=${endDate}&P_SNO=${serviceNumber}`
            );
            setSubcontractData(res.data.ResultSet || []);
          } catch {
            setSubcontractData([]);
          }
        }
      };

      try {
        if (selectedTab === "Tab1") {
          if (searchMode && serviceNumber) {
            await fetchRfidDetailsById();
          } else {
            await fetchRfidDetails();
          }
        } else if (selectedTab === "Tab2") {
          if (searchMode && serviceNumber) {
            await fetchSubcontractDetailsById();
          } else {
            await fetchSubcontractDetails();
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedDate,
    selectedTab,
    selectedLocation,
    searchMode,
    startDate,
    endDate,
    triggerSearch,
  ]);

  const renderEndorsementActions = () => (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
      <EndorseButton
        variant="contained"
        startIcon={<AssignmentTurnedInIcon />}
        onClick={openEndorseDialog}
      >
        {selectedTab === "Tab1" ? "CDL Endorsement" : "Sub Endorsement"}
      </EndorseButton>
      <SearchButton
        variant="outlined"
        onClick={activeSection === "attendance" ? showEndorsements : showAttendance}
        startIcon={
          activeSection === "attendance" ? (
            <AssignmentTurnedInIcon />
          ) : (
            <SearchIcon />
          )
        }
      >
        {activeSection === "attendance" ? "View Endorsements" : "Back to Attendance"}
      </SearchButton>
    </Stack>
  );

  /* ==================== SEARCH CONTROLS ==================== */
  const renderSearchControls = () => (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={1.5}
        alignItems={isMobile ? "stretch" : "center"}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <SearchButton
            variant="outlined"
            onClick={toggleSearchMode}
            startIcon={<SearchIcon />}
          >
            {searchMode ? "Daily View" : "Search Employee"}
          </SearchButton>
          {renderEndorsementActions()}
        </Stack>

        {searchMode && (
          <IconButton
            onClick={toggleExpandSearch}
            sx={{
              alignSelf: isMobile ? "flex-end" : "center",
              backgroundColor: BRAND.soft,
              color: BRAND.main,
              "&:hover": { backgroundColor: BRAND.border },
            }}
            size="small"
          >
            {expandedSearch ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Stack>

      <Collapse in={expandedSearch && searchMode}>
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: "14px",
            background: BRAND.softer,
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <SearchField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon
                        sx={{ color: BRAND.main, fontSize: 18 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SearchField
                label="End Date"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon
                        sx={{ color: BRAND.main, fontSize: 18 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SearchField
                label={selectedTab === "Tab1" ? "Service Number" : "User ID"}
                value={serviceNumber}
                onChange={handleServiceNumberChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setTriggerSearch((prev) => !prev);
                }}
                fullWidth
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon sx={{ color: BRAND.main, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setTriggerSearch((prev) => !prev)}
                        edge="end"
                        size="small"
                        sx={{
                          backgroundColor: BRAND.main,
                          color: "white",
                          "&:hover": { backgroundColor: BRAND.dark },
                        }}
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );

  /* ==================== DAILY FILTER BAR (Single Line) ==================== */
  const renderDailyFilter = (showLocation = true) => (
    <Box
      sx={{
        mb: 2.5,
        p: 2,
        borderRadius: "14px",
        background: BRAND.softer,
        border: `1px solid ${BRAND.border}`,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        flexWrap="nowrap"
        sx={{ width: "100%" }}
      >
        {/* Date Field */}
        <SearchField
          label="Date"
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          InputLabelProps={{ shrink: true }}
          size={isMobile ? "small" : "medium"}
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiInputBase-root": {
              fontSize: { xs: "13px", sm: "14px" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarTodayIcon
                  sx={{ color: BRAND.main, fontSize: { xs: 16, sm: 18 } }}
                />
              </InputAdornment>
            ),
          }}
        />

        {/* Location Select */}
        {showLocation && (
          <FormControl
            size={isMobile ? "small" : "medium"}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <InputLabel id="location-select-label">Location</InputLabel>
            <Select
              labelId="location-select-label"
              label="Location"
              value={selectedLocation}
              onChange={handleLocationChange}
              sx={{
                borderRadius: "10px",
                backgroundColor: "#ffffff",
                fontSize: { xs: "13px", sm: "14px" },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: BRAND.main,
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: BRAND.main,
                },
              }}
              startAdornment={
                <InputAdornment position="start">
                  <LocationOnIcon
                    sx={{ color: BRAND.main, fontSize: { xs: 16, sm: 18 } }}
                  />
                </InputAdornment>
              }
            >
              {LOCATIONS.map((loc) => (
                <MenuItem key={loc.value} value={loc.value}>
                  {loc.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>
    </Box>
  );

  /* ==================== TOTAL COUNT BADGE RENDER ==================== */
  const renderCountBadge = (count, label) => (
    <CountBadge>
      <Stack direction="row" spacing={1} alignItems="center">
        <PersonIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.3px",
            fontSize: { xs: "12px", sm: "13px" },
          }}
        >
          {label}
        </Typography>
      </Stack>
      <Chip
        label={`Total: ${count}`}
        size="small"
        sx={{
          backgroundColor: "rgba(255,255,255,0.22)",
          color: "#ffffff",
          fontWeight: 800,
          fontSize: { xs: "11px", sm: "12.5px" },
          height: { xs: "24px", sm: "26px" },
          border: "1px solid rgba(255,255,255,0.35)",
          "& .MuiChip-label": { px: { xs: 1, sm: 1.5 } },
        }}
      />
    </CountBadge>
  );

  /* ==================== ATTENDANCE TABLE (Always Used, Mobile Optimized) ==================== */
  const renderAttendanceTable = (data, type) => {
    const isRfid = type === "rfid";

    return (
      <Box
        sx={{
          maxHeight: "480px",
          overflow: "auto",
          borderRadius: "14px",
          border: `1px solid ${BRAND.border}`,
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { width: "6px", height: "6px" },
          "&::-webkit-scrollbar-track": {
            backgroundColor: BRAND.softer,
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: BRAND.border,
            borderRadius: "4px",
            "&:hover": { backgroundColor: BRAND.main },
          },
        }}
      >
        {isRfid && (
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
            alignItems="center"
            sx={{
              px: 1,
              height: { xs: "30px", sm: "34px" },
              boxSizing: "border-box",
              position: "sticky",
              top: 0,
              zIndex: 3,
              backgroundColor: "#ffffff",
            }}
          >
            <TimeBadge synced={false}>Not Synchronized</TimeBadge>
            <TimeBadge synced>Synchronized</TimeBadge>
          </Stack>
        )}
        <Table
          size="small"
          stickyHeader
          sx={{
            width: "100%",
            tableLayout: isMobile ? "fixed" : "auto",
            "& .MuiTableCell-stickyHeader": {
              top: isRfid ? (isMobile ? "30px" : "34px") : 0,
            },
          }}
        >
          <StyledTableHead>
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  width: isMobile ? "28%" : "auto",
                }}
              >
                {isRfid ? "Service No" : "User ID"}
              </TableCell>
              {isRfid && (
                <TableCell
                  align="center"
                  sx={{
                    width: isMobile ? "22%" : "auto",
                  }}
                >
                  Card No
                </TableCell>
              )}
              <TableCell
                align="left"
                sx={{
                  width: isMobile ? "30%" : "auto",
                }}
              >
                Name
              </TableCell>
              {searchMode && (
                <TableCell
                  align="center"
                  sx={{
                    width: isMobile ? "20%" : "auto",
                  }}
                >
                  Date
                </TableCell>
              )}
              <TableCell
                align="center"
                sx={{
                  width: isMobile ? "20%" : "auto",
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  justifyContent="center"
                  alignItems="center"
                >
                  <LoginIcon sx={{ fontSize: isMobile ? 11 : 14 }} />
                  <span>In</span>
                </Stack>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  width: isMobile ? "20%" : "auto",
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.5}
                  justifyContent="center"
                  alignItems="center"
                >
                  <LogoutIcon sx={{ fontSize: isMobile ? 11 : 14 }} />
                  <span>Out</span>
                </Stack>
              </TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {data.length > 0 ? (
              data.map((row, i) => {
                const inTime = isRfid ? row.Intime : row.Sub_InTime;
                const outTime = isRfid ? row.OutTime : row.Sub_OutTime;
                const inSynced =
                  isRfid && Boolean(String(row.SystemInTime ?? "").trim());
                const outSynced =
                  isRfid && Boolean(String(row.SystemOutTime ?? "").trim());
                const id = isRfid ? row.Service_no : row.Sub_UID;
                const name = isRfid ? row.Name : row.Sub_Name;
                const cardNo = isRfid ? row.Prox_no : null;

                return (
                  <StyledTableRow key={i}>
                    <StyledTableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: BRAND.main,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {id || "-"}
                    </StyledTableCell>
                    {isRfid && (
                      <StyledTableCell align="center">
                        <Chip
                          label={cardNo || "-"}
                          size="small"
                          sx={{
                            fontSize: { xs: "10px", sm: "11px" },
                            height: { xs: "20px", sm: "22px" },
                            backgroundColor: BRAND.soft,
                            color: BRAND.main,
                            fontWeight: 700,
                            maxWidth: "100%",
                            "& .MuiChip-label": {
                              px: { xs: 0.75, sm: 1 },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      </StyledTableCell>
                    )}
                    <StyledTableCell
                      align="left"
                      sx={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {name || "-"}
                    </StyledTableCell>
                    {searchMode && (
                      <StyledTableCell align="center">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          sx={{ fontSize: { xs: "10.5px", sm: "12px" } }}
                        >
                          {inTime ? dayjs(inTime).format("MMM DD") : "-"}
                        </Typography>
                      </StyledTableCell>
                    )}
                    <StyledTableCell align="center">
                      <TimeBadge
                        variant="in"
                        synced={isRfid ? inSynced : undefined}
                      >
                        {inTime ? dayjs(inTime).format("HH:mm") : "--:--"}
                      </TimeBadge>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <TimeBadge
                        variant="out"
                        synced={isRfid ? outSynced : undefined}
                      >
                        {outTime ? dayjs(outTime).format("HH:mm") : "--:--"}
                      </TimeBadge>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={(isRfid ? 5 : 4) + (searchMode ? 1 : 0)}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      backgroundColor: BRAND.soft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 12px",
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 32, color: BRAND.main }} />
                  </Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    No data found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    );
  };

  /* ==================== RENDER CONTENT ==================== */
  const renderEndorsements = () => {
    const isCdl = selectedTab === "Tab1";
    const rows = isCdl ? endorsements.cdl : endorsements.subcontract;

    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          {renderEndorsementActions()}
        </Box>
        {endorsementsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {endorsementsError}
          </Alert>
        )}

        {endorsementsLoading ? (
          <Loader text="Loading endorsements..." />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" aria-label="Endorsements" sx={{ minWidth: 850 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: BRAND.main }}>
                  <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>
                    Date and time
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {isCdl ? "Clock No" : "User ID"}
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {isCdl ? "Card No" : "NIC"}
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>Location</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                    {isCdl ? "Reason" : "Remark"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      No endorsements to view.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const dateValue = isCdl
                      ? row.HBL_BARCODE_DATE
                      : row.TFT_DATETIME;
                    const parsedDate = dateValue ? new Date(dateValue) : null;
                    const dateLabel =
                      parsedDate && !Number.isNaN(parsedDate.getTime())
                        ? parsedDate.toLocaleString()
                        : dateValue || "-";
                    const key = isCdl
                      ? row.HBL_ID
                      : `${row.TFT_UID}-${row.TFT_DATETIME}`;

                    return (
                      <TableRow
                        hover
                        key={key}
                        sx={{ "&:nth-of-type(even)": { bgcolor: BRAND.softer } }}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{dateLabel}</TableCell>
                        <TableCell sx={{ color: BRAND.main, fontWeight: 700 }}>
                          {isCdl ? row.HBL_CLOCK_NO : row.TFT_UID}
                        </TableCell>
                        <TableCell>{row.NAME || "-"}</TableCell>
                        <TableCell>
                          {isCdl ? row.HBL_BARCODE_NO || "-" : row.TFT_NIC || "-"}
                        </TableCell>
                        <TableCell>
                          {isCdl ? row.HBL_LOCATION || "-" : row.TFT_LOCATION || "-"}
                        </TableCell>
                        <TableCell>
                          {(isCdl ? row.HBL_REASON : row.TFT_REMARK) || "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    );
  };

  const renderComponent = () => {
    if (activeSection === "endorsements") return renderEndorsements();
    if (loading) return <Loader text="Loading Attendance Data..." />;

    switch (selectedTab) {
      case "Tab1":
        return (
          <Box>
            {renderSearchControls()}
            {!searchMode && renderDailyFilter(true)}
            {renderCountBadge(
              rfidData.length,
              searchMode ? "CDPLC Search Results" : "CDPLC Daily Attendance"
            )}
            {renderAttendanceTable(rfidData, "rfid")}
          </Box>
        );

      case "Tab2":
        return (
          <Box>
            {renderSearchControls()}
            {!searchMode && renderDailyFilter(false)}
            {renderCountBadge(
              subcontractData.length,
              searchMode
                ? "Subcontract Search Results"
                : "Subcontract Daily Attendance"
            )}
            {renderAttendanceTable(subcontractData, "sub")}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <PageWrapper>
      {/* ==================== ATTENDANCE TYPE TABS ==================== */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          p: 0.75,
          borderRadius: "14px",
          backgroundColor: "#ffffff",
          boxShadow: `0 4px 20px rgba(0, 74, 173, 0.12)`,
          border: `1px solid ${BRAND.border}`,
          width: "100%",
          maxWidth: "500px",
          mb: 3,
        }}
      >
        {["Tab1", "Tab2"].map((tab) => (
          <TabButton
            key={tab}
            active={selectedTab === tab ? 1 : 0}
            onClick={() => handleTabChange(tab)}
            sx={{ flex: 1 }}
          >
            {tab === "Tab1" ? "CDPLC" : "SUBCONTRACT"}
          </TabButton>
        ))}
      </Box>

      {/* ==================== MAIN CONTENT ==================== */}
      <StyledPaper elevation={0}>
        <Box
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.light} 100%)`,
            color: "white",
            position: "relative",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
            },
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            position="relative"
            zIndex={1}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                backgroundColor: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(10px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            >
              <PersonIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                sx={{ fontWeight: 700, lineHeight: 1.2 }}
              >
                {activeSection === "endorsements"
                  ? "RFID Endorsements"
                  : selectedTab === "Tab1"
                    ? "RFID Attendance"
                    : "Face Recognition Attendance"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "12px" }}
              >
                {activeSection === "endorsements"
                  ? "Read-only CDPLC and subcontract endorsement requests"
                  : selectedTab === "Tab1"
                    ? "CDPLC Employee Attendance Records"
                    : "Subcontract Employee Attendance Records"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>{renderComponent()}</Box>
      </StyledPaper>

      {/* ==================== ENDORSEMENT DIALOG ==================== */}
      <Dialog
        open={endorseOpen}
        onClose={closeEndorseDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "18px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.light} 100%)`,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 2,
            px: 3,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AssignmentTurnedInIcon />
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {selectedTab === "Tab1" ? "CDL Endorsement" : "Sub Contract Endorsement"}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Submit attendance endorsement
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={closeEndorseDialog}
            disabled={endorseSubmitting}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ p: { xs: 2, sm: 3 }, backgroundColor: BRAND.softer }}
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={selectedTab === "Tab1" ? cdlEmployees : subEmployees}
                value={
                  (selectedTab === "Tab1" ? cdlEmployees : subEmployees).find(
                    (employee) =>
                      (selectedTab === "Tab1"
                        ? employee.CED_SERVICE_NO
                        : employee.Sub_UID) === endorseForm.P_SNO
                  ) || null
                }
                onChange={(event, newValue) =>
                  handleEndorseFieldChange(
                    "P_SNO",
                    selectedTab === "Tab1"
                      ? newValue?.CED_SERVICE_NO || ""
                      : newValue?.Sub_UID || ""
                  )
                }
                getOptionLabel={(employee) =>
                  selectedTab === "Tab1"
                    ? `${employee.CED_SERVICE_NO || ""} - ${employee.CED_REPORT_NAME || ""} (${employee.CED_BARCODE_CARDNO || ""})`
                    : `${employee.Sub_UID || ""} - ${employee.Sub_Name || ""}`
                }
                isOptionEqualToValue={(option, value) =>
                  selectedTab === "Tab1"
                    ? option.CED_SERVICE_NO === value.CED_SERVICE_NO
                    : option.Sub_UID === value.Sub_UID
                }
                renderOption={(props, employee) => (
                  <li {...props}>
                    {selectedTab === "Tab1"
                      ? `${employee.CED_SERVICE_NO || ""} - ${employee.CED_REPORT_NAME || ""} (${employee.CED_BARCODE_CARDNO || ""})`
                      : `${employee.Sub_UID || ""} - ${employee.Sub_Name || ""}`}
                  </li>
                )}
                renderInput={(params) => (
                  <SearchField
                    {...params}
                    label={selectedTab === "Tab1" ? "Service Number" : "User ID"}
                    required
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <BadgeIcon sx={{ color: BRAND.main, fontSize: 18 }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel id="endorse-location-label">Location</InputLabel>
                <Select
                  labelId="endorse-location-label"
                  label="Location"
                  value={endorseForm.p_location}
                  onChange={(e) =>
                    handleEndorseFieldChange("p_location", e.target.value)
                  }
                  sx={{ borderRadius: "10px", backgroundColor: "#ffffff" }}
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon
                        sx={{ color: BRAND.main, fontSize: 18 }}
                      />
                    </InputAdornment>
                  }
                >
                  {LOCATIONS.map((loc) => (
                    <MenuItem key={loc.value} value={loc.value}>
                      {loc.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 700, color: "#455A64" }}
              >
                Status
              </Typography>
              <RadioGroup
                row
                value={endorseForm.p_hstatus}
                onChange={(e) =>
                  handleEndorseFieldChange("p_hstatus", e.target.value)
                }
                sx={{ gap: 2 }}
              >
                <FormControlLabel
                  value="I"
                  control={
                    <Radio
                      sx={{
                        color: BRAND.main,
                        "&.Mui-checked": { color: BRAND.success },
                      }}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <LoginIcon
                        sx={{ fontSize: 16, color: BRAND.success }}
                      />
                      <Typography variant="body2" fontWeight={700}>
                        In
                      </Typography>
                    </Stack>
                  }
                  sx={{
                    m: 0,
                    px: 2,
                    py: 0.75,
                    borderRadius: "10px",
                    border:
                      endorseForm.p_hstatus === "I"
                        ? `1.5px solid ${BRAND.success}`
                        : `1.5px solid ${BRAND.border}`,
                    backgroundColor:
                      endorseForm.p_hstatus === "I" ? "#E8F5E9" : "transparent",
                  }}
                />
                <FormControlLabel
                  value="O"
                  control={
                    <Radio
                      sx={{
                        color: BRAND.main,
                        "&.Mui-checked": { color: BRAND.warning },
                      }}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <LogoutIcon
                        sx={{ fontSize: 16, color: BRAND.warning }}
                      />
                      <Typography variant="body2" fontWeight={700}>
                        Out
                      </Typography>
                    </Stack>
                  }
                  sx={{
                    m: 0,
                    px: 2,
                    py: 0.75,
                    borderRadius: "10px",
                    border:
                      endorseForm.p_hstatus === "O"
                        ? `1.5px solid ${BRAND.warning}`
                        : `1.5px solid ${BRAND.border}`,
                    backgroundColor:
                      endorseForm.p_hstatus === "O" ? "#FFF3E0" : "transparent",
                  }}
                />
              </RadioGroup>
            </Grid>

            <Grid item xs={12} sm={6}>
              <SearchField
                label="Date"
                type="date"
                value={endorseForm.P_DATE_DATE}
                onChange={(e) =>
                  handleEndorseFieldChange("P_DATE_DATE", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon
                        sx={{ color: BRAND.main, fontSize: 18 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <SearchField
                label="Time"
                type="time"
                value={endorseForm.P_DATE_TIME}
                onChange={(e) =>
                  handleEndorseFieldChange("P_DATE_TIME", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            <Grid item xs={12}>
              <SearchField
                label="Reason"
                value={endorseForm.p_reason}
                onChange={(e) =>
                  handleEndorseFieldChange("p_reason", e.target.value)
                }
                fullWidth
                multiline
                rows={3}
                required
                placeholder="Enter the reason for endorsement..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1, backgroundColor: "#ffffff" }}>
          <Button
            onClick={closeEndorseDialog}
            disabled={endorseSubmitting}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              color: "#546E7A",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEndorseSubmit}
            disabled={endorseSubmitting}
            startIcon={<CheckCircleOutlineIcon />}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              background: `linear-gradient(135deg, ${BRAND.main} 0%, ${BRAND.light} 100%)`,
              boxShadow: `0 4px 14px rgba(0, 74, 173, 0.35)`,
              "&:hover": {
                background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.main} 100%)`,
                boxShadow: `0 6px 20px rgba(0, 74, 173, 0.5)`,
              },
              "&:disabled": { background: "#E0E0E0", boxShadow: "none" },
            }}
          >
            {endorseSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== TOP SNACKBAR ==================== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: { xs: 12, sm: 20 },
          zIndex: (theme) => theme.zIndex.snackbar,
        }}
      >
        <TopAlert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          alertseverity={snackbar.severity}
          iconMapping={{
            success: <CheckCircleOutlineIcon fontSize="inherit" />,
            error: <ErrorOutlineIcon fontSize="inherit" />,
            warning: <WarningAmberIcon fontSize="inherit" />,
            info: <NotificationsActiveIcon fontSize="inherit" />,
          }}
        >
          {snackbar.message}
        </TopAlert>
      </Snackbar>
    </PageWrapper>
  );
};

export default Rfid_Attendence;